import { Alert, Badge, Button, Card, CardBody, Spinner } from '@foundation/ui'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import styles from './sequences.$id.module.css'
import { api, type Sequence } from '@/lib/api'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/sequences/$id')({
  component: SequenceDetailPage,
})

const TEMPLATE_VARIABLES = [
  { variable: '{{customer_name}}', description: 'Customer full name' },
  { variable: '{{customer_email}}', description: 'Customer email address' },
  { variable: '{{product_name}}', description: 'Cancelled product/plan name' },
  { variable: '{{discount_offer}}', description: 'Discount offer text' },
  { variable: '{{reactivation_url}}', description: 'Link to reactivate subscription' },
  { variable: '{{company_name}}', description: 'Your company name' },
]

function TemplatePreview({ subject, body }: { subject: string; body: string }) {
  const highlightVariables = (text: string) => {
    // Split on {{variable}} patterns and highlight them
    const parts = text.split(/({{[^}]+}})/g)
    return parts.map((part, i) => {
      if (part.startsWith('{{') && part.endsWith('}}')) {
        return (
          <span key={i} className={styles.templateVariable ?? ''}>
            {part}
          </span>
        )
      }
      return <span key={i}>{part}</span>
    })
  }

  return (
    <div className={styles.emailPreview ?? ''}>
      <div className={styles.emailPreviewHeader ?? ''}>
        <div className={styles.emailPreviewSubject ?? ''}>
          <span className={styles.emailPreviewLabel ?? ''}>Subject:</span>
          <span>{highlightVariables(subject)}</span>
        </div>
      </div>
      <div className={styles.emailPreviewBody ?? ''}>{highlightVariables(body)}</div>
    </div>
  )
}

function SequenceDetailPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const [sequence, setSequence] = useState<Sequence | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .getSequence(id)
      .then((res) => setSequence(res.data))
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load sequence')
      })
      .finally(() => setIsLoading(false))
  }, [id])

  const handleToggleActive = () => {
    if (!sequence) return
    const action = sequence.isActive
      ? api.deactivateSequence(sequence.id)
      : api.activateSequence(sequence.id)

    action
      .then((res) => {
        setSequence(res.data)
        toast.success(sequence.isActive ? 'Sequence paused' : 'Sequence activated')
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to update sequence')
      })
  }

  const handleSetDefault = () => {
    if (!sequence) return
    api
      .setDefaultSequence(sequence.id)
      .then((res) => {
        setSequence(res.data)
        toast.success('Set as default sequence')
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to set default')
      })
  }

  const handleDelete = () => {
    if (!sequence) return
    if (
      !window.confirm(
        `Are you sure you want to delete "${sequence.name}"? This action cannot be undone.`
      )
    ) {
      return
    }

    api
      .deleteSequence(sequence.id)
      .then(() => {
        toast.success('Sequence deleted')
        void navigate({ to: '/sequences' })
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to delete sequence')
      })
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !sequence) {
    return (
      <div>
        <Link to="/sequences" className={styles.backLink}>
          &larr; Back to Sequences
        </Link>
        <Alert variant="danger" style={{ marginTop: '1rem' }}>
          {error ?? 'Sequence not found'}
        </Alert>
      </div>
    )
  }

  const statusBadge = sequence.isActive ? (
    <Badge variant="success">Active</Badge>
  ) : sequence.steps.length === 0 ? (
    <Badge variant="default">Draft</Badge>
  ) : (
    <Badge variant="warning">Paused</Badge>
  )

  const sortedSteps = [...sequence.steps].sort((a, b) => a.stepNumber - b.stepNumber)

  const totalSteps = sequence.steps.length

  return (
    <>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div>
            <Link to="/sequences" className={styles.backLink}>
              &larr; Back to Sequences
            </Link>
            <h1 className={styles.sequenceName}>
              {sequence.name} {statusBadge}
            </h1>
          </div>
        </div>
        <div className={styles.headerActions}>
          <Button variant="secondary" size="sm" onClick={handleToggleActive}>
            {sequence.isActive ? 'Pause' : 'Activate'}
          </Button>
          {!sequence.isDefault && (
            <Button variant="secondary" size="sm" onClick={handleSetDefault}>
              Set as Default
            </Button>
          )}
          <Button variant="danger" size="sm" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </div>

      {/* Info Row */}
      <div className={styles.infoRow}>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Created</span>
          <span className={styles.infoValue}>
            {new Date(sequence.createdAt).toLocaleDateString()}
          </span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Last Updated</span>
          <span className={styles.infoValue}>
            {new Date(sequence.updatedAt).toLocaleDateString()}
          </span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Email Steps</span>
          <span className={styles.infoValue}>{totalSteps}</span>
        </div>
        {sequence.isDefault && (
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Role</span>
            <span className={styles.infoValue}>Default Sequence</span>
          </div>
        )}
      </div>

      {sequence.description && (
        <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
          {sequence.description}
        </p>
      )}

      {/* Stats Grid */}
      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <CardBody>
            <div className={styles.statValue}>{totalSteps}</div>
            <div className={styles.statLabel}>Email Steps</div>
          </CardBody>
        </Card>
        <Card className={styles.statCard}>
          <CardBody>
            <div className={styles.statValue}>
              {sortedSteps.length > 0
                ? `${sortedSteps[sortedSteps.length - 1]?.delayDays ?? 0}d`
                : '0d'}
            </div>
            <div className={styles.statLabel}>Total Duration</div>
          </CardBody>
        </Card>
        <Card className={styles.statCard}>
          <CardBody>
            <div className={styles.statValue}>
              {sequence.steps.filter((s) => s.isActive).length}
            </div>
            <div className={styles.statLabel}>Active Steps</div>
          </CardBody>
        </Card>
        <Card className={styles.statCard}>
          <CardBody>
            <div className={styles.statValue}>
              {sequence.steps.filter((s) => s.discountOffer).length}
            </div>
            <div className={styles.statLabel}>With Offers</div>
          </CardBody>
        </Card>
      </div>

      {/* Email Steps Timeline */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Email Steps Timeline</h3>
        </div>

        {sortedSteps.length === 0 ? (
          <Card>
            <CardBody>
              <div className={styles.emptyTimeline}>
                <p>No email steps configured yet.</p>
                <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  Edit this sequence to add email steps.
                </p>
              </div>
            </CardBody>
          </Card>
        ) : (
          <Card>
            <CardBody>
              <div className={styles.timeline}>
                {sortedSteps.map((step) => (
                  <div key={step.id} className={styles.timelineItem}>
                    <div
                      className={`${styles.timelineDot} ${
                        !step.isActive ? styles.timelineDotInactive : ''
                      }`}
                    />
                    <div className={styles.timelineContent}>
                      <div className={styles.timelineHeader}>
                        <span className={styles.timelineTitle}>
                          Step {step.stepNumber}
                          {!step.isActive && (
                            <Badge variant="default" style={{ marginLeft: '0.5rem' }}>
                              Inactive
                            </Badge>
                          )}
                        </span>
                        <span className={styles.timelineDelay}>
                          {step.delayDays === 0 ? 'Immediately' : `Day ${step.delayDays}`}
                        </span>
                      </div>
                      <div className={styles.timelineSubject}>{step.subjectTemplate}</div>
                      <div className={styles.timelineStats}>
                        {step.ctaText && (
                          <span className={styles.timelineStat}>CTA: {step.ctaText}</span>
                        )}
                        {step.discountOffer && (
                          <span className={styles.timelineStat}>Offer: {step.discountOffer}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}
      </div>

      {/* Sequence Details */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Step Details</h3>
        </div>
        {sortedSteps.map((step) => (
          <Card key={step.id} style={{ marginBottom: '1rem' }}>
            <CardBody>
              <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                Step {step.stepNumber}
                {!step.isActive && (
                  <Badge variant="default" style={{ marginLeft: '0.5rem' }}>
                    Inactive
                  </Badge>
                )}
              </h4>
              <TemplatePreview subject={step.subjectTemplate} body={step.bodyTemplate} />
              <div
                style={{
                  display: 'flex',
                  gap: '1rem',
                  fontSize: '0.8125rem',
                  color: 'var(--text-muted)',
                  marginBottom: '0.75rem',
                }}
              >
                <span>Delay: {step.delayDays === 0 ? 'Immediate' : `${step.delayDays} days`}</span>
                {step.ctaText && <span>CTA: {step.ctaText}</span>}
                {step.ctaUrl && (
                  <a
                    href={step.ctaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--color-primary)' }}
                  >
                    {step.ctaUrl}
                  </a>
                )}
                {step.discountOffer && <span>Offer: {step.discountOffer}</span>}
              </div>
              <div className={styles.variableRef ?? ''}>
                <div className={styles.variableRefTitle ?? ''}>Variable Reference</div>
                <div className={styles.variableList ?? ''}>
                  {TEMPLATE_VARIABLES.map((v) => (
                    <span
                      key={v.variable}
                      className={styles.variableTag ?? ''}
                      title={v.description}
                    >
                      {v.variable}
                    </span>
                  ))}
                </div>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>
    </>
  )
}
